import React, { useState, useEffect, useRef } from 'react';
import './RelatedDesigns.css';
import { API_URL } from '../config';

/**
 * Scores how "related" a community design is to the current prompt.
 * Returns a 0-100 score based on keyword overlap.
 */
function scoreRelevance(designPrompt, currentPrompt) {
  if (!designPrompt || !currentPrompt) return 0;
  const normalize = (s) =>
    s.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(/\s+/).filter(w => w.length > 2);
  const sourceWords = new Set(normalize(currentPrompt));
  const targetWords = normalize(designPrompt);
  if (sourceWords.size === 0 || targetWords.length === 0) return 0;
  const matches = targetWords.filter(w => sourceWords.has(w)).length;
  return (matches / Math.max(sourceWords.size, targetWords.length)) * 100;
}

const CACHE_KEY = 'fashionai_related_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 min

function getCachedDesigns() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function setCachedDesigns(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

export default function RelatedDesigns({ currentPrompt, currentDesignId, onUsePrompt }) {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!currentPrompt || currentPrompt.trim().length < 3) {
      setRelated([]);
      return;
    }

    // Cancel previous fetch
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setRelated([]);

    async function fetchAndScore() {
      try {
        // Try cache first
        let pool = getCachedDesigns();
        if (!pool) {
          const res = await fetch(`${API_URL}/designs?limit=80&offset=0&sort=likes`, {
            signal: ctrl.signal,
          });
          if (!res.ok) throw new Error('fetch failed');
          const { designs } = await res.json();
          pool = designs || [];
          setCachedDesigns(pool);
        }

        // Score each design, exclude current one
        const scored = pool
          .filter(d => d.image_url && d.id !== currentDesignId)
          .map(d => ({
            ...d,
            _score: scoreRelevance(d.prompt, currentPrompt),
          }))
          .sort((a, b) => b._score - a._score);

        // Take top 6 with score > 0, or just top 6 by likes if no keyword matches
        const topRelated = scored.filter(d => d._score > 0).slice(0, 6);
        const fallback = scored.slice(0, 6);
        setRelated(topRelated.length >= 2 ? topRelated : fallback);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('[RelatedDesigns] fetch error:', e);
          setRelated([]);
        }
      } finally {
        setLoading(false);
      }
    }

    // Debounce slightly to avoid firing on every keystroke
    const t = setTimeout(fetchAndScore, 400);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [currentPrompt, currentDesignId]);

  if (!currentPrompt || currentPrompt.trim().length < 3) return null;

  return (
    <div className="related-designs">
      <div className="related-designs__header">
        <span className="related-designs__label">✨ You might also like</span>
      </div>

      <div className="related-designs__strip">
        {loading
          ? Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="related-skeleton" />
            ))
          : related.length === 0
          ? <span className="related-designs__empty">No similar designs yet — yours might be first!</span>
          : related.map(d => (
              <RelatedCard key={d.id} design={d} onUsePrompt={onUsePrompt} />
            ))
        }
      </div>
    </div>
  );
}

function RelatedCard({ design, onUsePrompt }) {
  const handleClick = () => {
    if (onUsePrompt) onUsePrompt(design.prompt);
  };

  return (
    <div className="related-card" onClick={handleClick} title={design.prompt}>
      <img
        className="related-card__img"
        src={design.image_url}
        alt={design.prompt}
        loading="lazy"
        onError={e => { e.target.style.display = 'none'; }}
      />
      <div className="related-card__body">
        <p className="related-card__prompt">{design.prompt}</p>
      </div>
      <div className="related-card__use">Use this prompt →</div>
    </div>
  );
}
